import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL ?? '';

const TrialProfileSetup: React.FC = () => {
  const { token, user, setAuthFromTokenResponse } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!user) {
      return;
    }
    if (user.hasActiveSubscription && user.needsProfileCompletion) {
      return;
    }
    navigate(user.hasActiveSubscription ? '/' : '/subscription', { replace: true });
  }, [navigate, user]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    const normalized = username.trim();
    if (!normalized) {
      setError('ユーザーネームを入力してください。');
      return;
    }
    if (normalized.length > 15) {
      setError('ユーザーネームは15文字以内で入力してください。');
      return;
    }

    if (!token) {
      setError('認証情報が見つかりません。再度ログインしてください。');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/auth/trial-profile`,
        { username: normalized },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const isSessionReady = await setAuthFromTokenResponse(response.data);
      if (!isSessionReady) {
        setError('セッション確認に失敗しました。再ログインが必要です。');
        return;
      }
      navigate('/', { replace: true });
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (detail === 'trial_username_reserved') {
        setError('そのユーザーネームは使用できません。別の名前を入力してください。');
      } else if (detail === 'username_required') {
        setError('ユーザーネームを入力してください。');
      } else {
        setError('ユーザーネームの設定に失敗しました。時間をおいて再度お試しください。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" align="center" gutterBottom>
          ユーザーネーム設定
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          利用を開始する前に、今後利用するユーザーネームを設定してください。
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="ユーザーネーム"
            name="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isSubmitting}
            inputProps={{ maxLength: 15 }}
            helperText="15文字以内で入力してください"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, py: 1.5 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              '保存'
            )}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default TrialProfileSetup;
